import { useState, useEffect, useRef } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FloppyDisk, Plus, X, UploadSimple, Image } from "@phosphor-icons/react";

const STANDARD_HOTEL_INCLUSIONS = [
  "Daily complimentary breakfast",
  "USD 100 hotel credit",
  "Upgrade to next category, subject to availability",
  "Early check-in and Late check-out, subject to availability",
  "VIP Tagging and Amenities",
  "All meals (full board)",
  "Airport transfers",
  "Seaplane / boat transfers",
  "Honeymoon / anniversary amenities",
  "Dedicated concierge support",
  "Complimentary Wi-Fi",
  "Minibar included",
  "Spa credit / treatment",
  "Club lounge access",
];

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const [settings, setSettings] = useState({
    consultant_name: "",
    consultant_designation: "",
    consultant_phone: "",
    consultant_email: "",
    consultant_website: "",
    agency_name: "Muskan Travel Associates Private Limited",
    agency_tagline: "Travel · Corporate · Cruises · Visa",
    agency_address: "",
    agency_logo_path: "",
    reference_format: "MTA-{YYYY}-{NNN}",
    default_terms: [],
    default_inclusions: [],
    rate_overrides: {},
  });

  const [newTerm, setNewTerm] = useState("");
  const [newInclusion, setNewInclusion] = useState("");
  const [newOverrideCurrency, setNewOverrideCurrency] = useState("");
  const [newOverrideRate, setNewOverrideRate] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    // Load logo preview if path exists
    if (settings.agency_logo_path) {
      loadLogoPreview(settings.agency_logo_path);
    }
  }, [settings.agency_logo_path]);

  const fetchSettings = async () => {
    try {
      const res = await api.get("/settings");
      setSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogoPreview = async (path) => {
    if (!path) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/files/${path}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      const blobUrl = URL.createObjectURL(response.data);
      setLogoPreview(blobUrl);
    } catch (error) {
      console.error("Failed to load logo:", error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/upload/logo", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setSettings(prev => ({ ...prev, agency_logo_path: res.data.path }));
      toast.success("Logo uploaded successfully");
      
      // Create preview from uploaded file
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to upload logo");
      console.error("Upload error:", error);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addTerm = () => {
    if (newTerm.trim()) {
      setSettings({
        ...settings,
        default_terms: [...settings.default_terms, newTerm.trim()],
      });
      setNewTerm("");
    }
  };

  const removeTerm = (index) => {
    setSettings({
      ...settings,
      default_terms: settings.default_terms.filter((_, i) => i !== index),
    });
  };

  const addInclusion = () => {
    if (newInclusion.trim()) {
      const current = settings.default_inclusions || [];
      if (current.includes(newInclusion.trim())) {
        toast.info("Inclusion already in defaults");
        return;
      }
      setSettings({
        ...settings,
        default_inclusions: [...current, newInclusion.trim()],
      });
      setNewInclusion("");
    }
  };

  const removeInclusion = (index) => {
    setSettings({
      ...settings,
      default_inclusions: settings.default_inclusions.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Settings</p>
          <h1 className="font-serif text-4xl text-navy">Preferences</h1>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
          data-testid="save-settings-btn"
        >
          <FloppyDisk size={18} />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Agency Branding with Logo */}
      <div className="bg-white border border-border p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Agency Branding
        </h2>
        
        {/* Logo Upload Section */}
        <div className="mb-6">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-3 block">Agency Logo</Label>
          <div className="flex items-center gap-6">
            <div 
              className="w-32 h-32 border-2 border-dashed border-border flex items-center justify-center bg-cream overflow-hidden cursor-pointer hover:border-navy transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {logoPreview ? (
                <img src={logoPreview} alt="Agency Logo" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center p-4">
                  <Image size={32} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-xs text-muted-foreground">Click to upload</p>
                </div>
              )}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="gap-2"
                data-testid="upload-logo-btn"
              >
                <UploadSimple size={16} />
                {uploadingLogo ? "Uploading..." : "Upload Logo"}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Recommended: 200x200px, PNG or JPG, max 5MB
              </p>
              <p className="text-xs text-muted-foreground">
                Logo will appear on PDF proposals
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Agency Name</Label>
            <Input
              value={settings.agency_name}
              onChange={(e) => setSettings({ ...settings, agency_name: e.target.value })}
              className="bg-cream border-border"
              data-testid="settings-agency-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tagline</Label>
            <Input
              value={settings.agency_tagline}
              onChange={(e) => setSettings({ ...settings, agency_tagline: e.target.value })}
              className="bg-cream border-border"
            />
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Agency Address</Label>
          <Textarea
            value={settings.agency_address || ""}
            onChange={(e) => setSettings({ ...settings, agency_address: e.target.value })}
            placeholder="123 Business District, City, Country"
            className="bg-cream border-border"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reference Number Format</Label>
          <Input
            value={settings.reference_format}
            onChange={(e) => setSettings({ ...settings, reference_format: e.target.value })}
            placeholder="MTA-{DDMMYYYY}-{N}"
            className="bg-cream border-border max-w-md"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Available: {'{DDMMYYYY}'} (date), {'{YYYYMMDD}'}, {'{DD}'}, {'{MM}'}, {'{YYYY}'}, {'{YY}'}, {'{N}'} (count), {'{NNN}'} (padded)
          </p>
          <p className="text-xs text-muted-foreground">
            Example: MTA-{'{DDMMYYYY}'}-{'{N}'} → MTA-01042026-1
          </p>
        </div>
      </div>

      {/* Consultant Details */}
      <div className="bg-white border border-border p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Consultant Details
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          These details will be pre-filled on every new proposal.
        </p>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
            <Input
              value={settings.consultant_name}
              onChange={(e) => setSettings({ ...settings, consultant_name: e.target.value })}
              placeholder="Pratyush Sareen"
              className="bg-cream border-border"
              data-testid="settings-consultant-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Designation</Label>
            <Input
              value={settings.consultant_designation}
              onChange={(e) => setSettings({ ...settings, consultant_designation: e.target.value })}
              placeholder="Director — Travel"
              className="bg-cream border-border"
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone / WhatsApp</Label>
            <Input
              value={settings.consultant_phone}
              onChange={(e) => setSettings({ ...settings, consultant_phone: e.target.value })}
              placeholder="+91 XXXXX XXXXX"
              className="bg-cream border-border"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
            <Input
              value={settings.consultant_email}
              onChange={(e) => setSettings({ ...settings, consultant_email: e.target.value })}
              placeholder="pratyush@muskantravel.com"
              className="bg-cream border-border"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Website</Label>
            <Input
              value={settings.consultant_website}
              onChange={(e) => setSettings({ ...settings, consultant_website: e.target.value })}
              placeholder="www.muskantravel.com"
              className="bg-cream border-border"
            />
          </div>
        </div>
      </div>

      {/* Default Terms */}
      <div className="bg-white border border-border p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Default Terms & Conditions
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          These terms will be pre-loaded on every new proposal.
        </p>
        <div className="space-y-2 mb-4">
          {settings.default_terms.map((term, i) => (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-gold font-semibold text-sm min-w-[24px]">{i + 1}.</span>
              <p className="flex-1 text-sm text-muted-foreground bg-cream p-2">{term}</p>
              <button
                onClick={() => removeTerm(i)}
                className="text-muted-foreground hover:text-destructive p-1"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value)}
            placeholder="Add a new default term..."
            className="bg-cream border-border"
            onKeyPress={(e) => e.key === "Enter" && addTerm()}
          />
          <Button
            variant="outline"
            onClick={addTerm}
            className="gap-1"
          >
            <Plus size={16} /> Add
          </Button>
        </div>
      </div>

      {/* Default Hotel Inclusions */}
      <div className="bg-white border border-border p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Default Hotel Inclusions
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Checked inclusions will be pre-selected on every new hotel block.
        </p>

        {/* Standard inclusions checkboxes */}
        <div className="grid grid-cols-3 gap-2.5 mb-5">
          {STANDARD_HOTEL_INCLUSIONS.map((inc) => (
            <div key={inc} className="flex items-center gap-2">
              <Checkbox
                id={`default-inc-${inc}`}
                checked={(settings.default_inclusions || []).includes(inc)}
                onCheckedChange={(checked) => {
                  const current = settings.default_inclusions || [];
                  const updated = checked
                    ? [...current, inc]
                    : current.filter((i) => i !== inc);
                  setSettings({ ...settings, default_inclusions: updated });
                }}
                className="border-border data-[state=checked]:bg-navy data-[state=checked]:border-navy"
              />
              <label htmlFor={`default-inc-${inc}`} className="text-xs cursor-pointer">{inc}</label>
            </div>
          ))}
        </div>

        {/* Custom saved inclusions */}
        {(settings.default_inclusions || []).filter((inc) => !STANDARD_HOTEL_INCLUSIONS.includes(inc)).length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Custom Defaults</p>
            <div className="flex flex-wrap gap-2">
              {(settings.default_inclusions || []).filter((inc) => !STANDARD_HOTEL_INCLUSIONS.includes(inc)).map((inc, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-xs bg-cream px-3 py-1.5 text-muted-foreground border border-border"
                >
                  {inc}
                  <button
                    onClick={() => {
                      setSettings({
                        ...settings,
                        default_inclusions: (settings.default_inclusions || []).filter((x) => x !== inc),
                      });
                    }}
                    className="text-muted-foreground hover:text-destructive ml-1"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={newInclusion}
            onChange={(e) => setNewInclusion(e.target.value)}
            placeholder="Add a custom default inclusion..."
            className="bg-cream border-border"
            onKeyPress={(e) => e.key === "Enter" && addInclusion()}
          />
          <Button
            variant="outline"
            onClick={addInclusion}
            className="gap-1"
          >
            <Plus size={16} /> Add
          </Button>
        </div>
      </div>

      {/* Currency Rate Overrides */}
      <div className="bg-white border border-border p-6 mb-6">
        <h2 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground pb-4 border-b border-cream-mid mb-5">
          Currency Rate Overrides (to INR)
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Override live exchange rates with your own rates. These will be used instead of the live rate for INR conversion. Leave empty to use live rates.
        </p>

        {Object.keys(settings.rate_overrides || {}).length > 0 && (
          <div className="space-y-2 mb-4">
            {Object.entries(settings.rate_overrides || {}).map(([curr, rate]) => (
              <div key={curr} className="flex items-center gap-3 bg-cream p-2 border border-border">
                <span className="text-xs font-bold text-navy w-12">{curr}</span>
                <span className="text-xs text-muted-foreground">=</span>
                <Input
                  type="number"
                  step="0.01"
                  value={rate}
                  onChange={(e) => {
                    const updated = { ...settings.rate_overrides, [curr]: e.target.value };
                    setSettings({ ...settings, rate_overrides: updated });
                  }}
                  className="bg-white border-border w-32 h-8 text-sm"
                />
                <span className="text-xs text-muted-foreground">INR</span>
                <button
                  onClick={() => {
                    const updated = { ...settings.rate_overrides };
                    delete updated[curr];
                    setSettings({ ...settings, rate_overrides: updated });
                  }}
                  className="text-muted-foreground hover:text-destructive ml-auto"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Input
            value={newOverrideCurrency}
            onChange={(e) => setNewOverrideCurrency(e.target.value.toUpperCase())}
            placeholder="Currency (e.g. AED)"
            className="bg-cream border-border w-32"
            maxLength={4}
          />
          <Input
            type="number"
            step="0.01"
            value={newOverrideRate}
            onChange={(e) => setNewOverrideRate(e.target.value)}
            placeholder="Rate in INR"
            className="bg-cream border-border w-40"
          />
          <Button
            variant="outline"
            onClick={() => {
              if (newOverrideCurrency.trim() && newOverrideRate) {
                setSettings({
                  ...settings,
                  rate_overrides: {
                    ...(settings.rate_overrides || {}),
                    [newOverrideCurrency.trim()]: parseFloat(newOverrideRate),
                  },
                });
                setNewOverrideCurrency("");
                setNewOverrideRate("");
              }
            }}
            className="gap-1"
          >
            <Plus size={16} /> Add
          </Button>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
        >
          <FloppyDisk size={18} />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
